// 필드 저장(dungeonhero.field.v1): 균열 조각 수·균열 클리어 수·숨은 던전 발견. 새 키만 쓴다(기존 저장 형식 불변).
// 장비 저장(game/gear.ts)과 같은 규칙: 주 키 + 정상 사본(.good). 손상이면 원문을 .bak에 남기고 정상 사본으로(없으면 새로) 이어 쓴다.
// 미래 버전(v > 1)은 읽기 전용(덮어쓰지 않고, 이번 세션 값은 메모리에만). 쓰기는 고정 스텝 밖(flush)에서만 한다.
import type { SaveStorage } from '../save';
import { FIELD, FIELD_SAVE_KEY, FIELD_SAVE_VERSION } from '../../shared/field/constants';

export interface FieldSaveData {
  v: number;
  shards: number;
  riftClears: number;
  hiddenFound: boolean;
}
export type FieldSaveMode = 'ok' | 'recovered' | 'future' | 'unavailable';

const fresh = (): FieldSaveData => ({ v: FIELD_SAVE_VERSION, shards: 0, riftClears: 0, hiddenFound: false });
const int = (v: unknown, hi: number): number | null => (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= hi ? v : null);

/** 원문 → 데이터(형식이 틀리면 null, 미래 버전이면 'future') */
export function parseFieldSave(raw: string | null): FieldSaveData | 'future' | null {
  if (raw === null) return null;
  let o: unknown;
  try { o = JSON.parse(raw); } catch { return null; }
  if (!o || typeof o !== 'object') return null;
  const r = o as Record<string, unknown>;
  const v = int(r.v, 1e6);
  if (v === null || v < 1) return null;
  if (v > FIELD_SAVE_VERSION) return 'future';
  const shards = int(r.shards, FIELD.shardCap), clears = int(r.riftClears, 1e9);
  if (shards === null || clears === null || typeof r.hiddenFound !== 'boolean') return null;
  return { v, shards, riftClears: clears, hiddenFound: r.hiddenFound };
}

export class FieldSave {
  data: FieldSaveData = fresh();
  mode: FieldSaveMode = 'ok';
  private dirty = false;
  constructor(private readonly storage: SaveStorage | null) { this.load(); }

  load(): void {
    const st = this.storage;
    if (!st) { this.mode = 'unavailable'; this.data = fresh(); return; }
    let raw: string | null;
    try { raw = st.getItem(FIELD_SAVE_KEY); } catch { this.mode = 'unavailable'; return; }
    const p = parseFieldSave(raw);
    if (p === 'future') { this.mode = 'future'; this.data = fresh(); return; }
    if (p) { this.data = p; this.mode = 'ok'; return; }
    if (raw === null) { this.data = fresh(); this.mode = 'ok'; return; }
    // 손상: 원문 보관 → 정상 사본으로 복구
    try { st.setItem(FIELD_SAVE_KEY + '.bak', raw); } catch { /* 보관 실패해도 이어 간다 */ }
    let good: FieldSaveData | 'future' | null = null;
    try { good = parseFieldSave(st.getItem(FIELD_SAVE_KEY + '.good')); } catch { good = null; }
    this.data = good && good !== 'future' ? good : fresh();
    this.mode = 'recovered';
    this.dirty = true;
  }

  get shards(): number { return this.data.shards; }
  addShards(n: number): number {
    this.data.shards = Math.max(0, Math.min(FIELD.shardCap, this.data.shards + n));
    this.dirty = true;
    return this.data.shards;
  }
  /** 조각을 n개 쓴다(모자라면 false, 바꾸지 않는다) */
  spendShards(n: number): boolean {
    if (this.data.shards < n) return false;
    this.data.shards -= n; this.dirty = true;
    return true;
  }
  addRiftClear(): void { this.data.riftClears++; this.dirty = true; }
  markHidden(): void { if (!this.data.hiddenFound) { this.data.hiddenFound = true; this.dirty = true; } }

  /** 고정 스텝 밖(GameSim.flushSaves)에서 부른다. 미래 버전·저장소 없음이면 쓰지 않는다 */
  flush(): boolean {
    if (!this.dirty) return true;
    if (this.mode === 'future' || this.mode === 'unavailable' || !this.storage) return false;
    const raw = JSON.stringify(this.data);
    try {
      this.storage.setItem(FIELD_SAVE_KEY, raw);
      this.storage.setItem(FIELD_SAVE_KEY + '.good', raw);
    } catch { return false; }
    this.dirty = false;
    return true;
  }
}
